import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Shield, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid, ToggleField } from "@/components/shared/form";
import {
  MasterBanner,
  MasterFilterBar,
  MasterHeader,
  MasterPage,
  MasterSearchInput,
  useMasterListShortcuts,
} from "@/components/shared/master-shell";
import { Badge, StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useCanManageUsers,
  useDepartments,
  useModules,
  usePermissions,
  useRolePermissionIds,
  useRoles,
  useSetUserRoles,
  useToggleRolePermission,
  useUpdateProfile,
  useUsers,
} from "@/features/users/api";
import { displayName } from "@/features/users/constants";
import {
  emptyProfileForm,
  profileFormSchema,
  type ProfileFormValues,
} from "@/features/users/schema";
import type { UserListRow } from "@/features/users/types";
import { apiJson, isApiConfigured } from "@/lib/api";
import { isSupabaseConfigured } from "@/lib/supabase";
import { cn } from "@/lib/utils";

type Tab = "users" | "permissions";

export function UsersPage() {
  return (
    <ErrorBoundary title="Users page crashed">
      <UsersPageInner />
    </ErrorBoundary>
  );
}

function UsersPageInner() {
  const [tab, setTab] = useState<Tab>("users");
  const [inviteOpen, setInviteOpen] = useState(false);
  const usersQ = useUsers();
  const rolesQ = useRoles();
  const permsQ = usePermissions();
  const rpQ = useRolePermissionIds();
  const modulesQ = useModules();
  const canManageQ = useCanManageUsers();

  const refetch = () => {
    void usersQ.refetch();
    void rolesQ.refetch();
    void permsQ.refetch();
    void rpQ.refetch();
    void modulesQ.refetch();
    void canManageQ.refetch();
  };

  return (
    <MasterPage>
      <MasterHeader
        title="Users & roles"
        icon={<Users />}
        countLabel={`${usersQ.data?.length ?? 0} users · ${rolesQ.data?.length ?? 0} roles · ${permsQ.data?.length ?? 0} permissions`}
        onRefresh={refetch}
        refreshing={
          usersQ.isFetching ||
          rolesQ.isFetching ||
          permsQ.isFetching ||
          rpQ.isFetching
        }
        actions={
          canManageQ.data && isApiConfigured() ? (
            <Button type="button" size="sm" onClick={() => setInviteOpen(true)}>
              <UserPlus />
              Invite
            </Button>
          ) : null
        }
      />

      {!isSupabaseConfigured ? (
        <MasterBanner>Connect Supabase environment variables to enable live data.</MasterBanner>
      ) : null}

      {canManageQ.data === false ? (
        <MasterBanner tone="danger">
          You need <code className="text-2xs">users.manage</code> (or admin role) to edit users and
          permissions. View-only may still work.
        </MasterBanner>
      ) : null}

      {!isApiConfigured() ? (
        <MasterBanner>
          Set <code className="text-2xs">VITE_API_URL</code> and run the Workers API to invite users by
          email. Or create accounts in the Supabase Auth dashboard.
        </MasterBanner>
      ) : (
        <MasterBanner>
          Invite sends a Supabase email, creates the profile, and assigns roles in one step.
        </MasterBanner>
      )}

      <InviteUserDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        roles={rolesQ.data ?? []}
        onInvited={() => void usersQ.refetch()}
      />

      <div className="flex gap-1 border-b border-border px-1">
        {(
          [
            ["users", "Users"],
            ["permissions", "Permissions"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={cn(
              "px-3 py-1.5 text-xs font-medium",
              tab === key
                ? "border-b-2 border-primary text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "users" ? (
        <UsersTab
          data={usersQ.data ?? []}
          isLoading={usersQ.isLoading}
          error={usersQ.error as Error | null}
          roles={rolesQ.data ?? []}
          canManage={canManageQ.data === true}
        />
      ) : (
        <PermissionsTab
          roles={rolesQ.data ?? []}
          permissions={permsQ.data ?? []}
          modules={modulesQ.data ?? []}
          rolePerms={rpQ.data ?? []}
          isLoading={permsQ.isLoading || rpQ.isLoading || rolesQ.isLoading}
          error={(permsQ.error || rpQ.error || rolesQ.error) as Error | null}
          canManage={canManageQ.data === true}
        />
      )}
    </MasterPage>
  );
}

function UsersTab({
  data,
  isLoading,
  error,
  roles,
  canManage,
}: {
  data: UserListRow[];
  isLoading: boolean;
  error: Error | null;
  roles: { id: string; name: string; code: string }[];
  canManage: boolean;
}) {
  const departmentsQ = useDepartments();
  const updateProfile = useUpdateProfile();
  const setRoles = useSetUserRoles();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("active");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editing, setEditing] = useState<UserListRow | null>(null);
  const [rolesUser, setRolesUser] = useState<UserListRow | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: emptyProfileForm,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((row) => {
      if (statusFilter === "active" && !row.is_active) return false;
      if (statusFilter === "inactive" && row.is_active) return false;
      if (roleFilter !== "all" && !row.role_codes.includes(roleFilter)) return false;
      if (!q) return true;
      return [
        row.userid,
        row.fname,
        row.lname,
        row.email,
        row.phone,
        row.department_name,
        ...row.role_names,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [data, search, statusFilter, roleFilter]);

  useMasterListShortcuts({ searchInputId: "users-search" });

  function openEdit(row: UserListRow) {
    setEditing(row);
    form.reset({
      userid: row.userid,
      fname: row.fname,
      lname: row.lname,
      email: row.email ?? "",
      phone: row.phone ?? "",
      department_id: row.department_id ?? "",
      is_active: row.is_active,
    });
  }

  function openRoles(row: UserListRow) {
    setRolesUser(row);
    setSelectedRoleIds([...row.role_ids]);
  }

  const submitProfile = form.handleSubmit(async (values) => {
    if (!editing) return;
    await updateProfile.mutateAsync({ id: editing.id, values });
    setEditing(null);
  });

  async function submitRoles() {
    if (!rolesUser) return;
    await setRoles.mutateAsync({ userId: rolesUser.id, roleIds: selectedRoleIds });
    setRolesUser(null);
  }

  return (
    <>
      {error ? <MasterBanner tone="danger">{error.message}</MasterBanner> : null}
      {!isLoading && data.length === 0 ? (
        <EmptyState
          title="No profiles yet"
          description="Create a user in Supabase Auth. Their profile row is created automatically on signup."
        />
      ) : (
        <>
          <MasterFilterBar shownLabel={`${filtered.length} shown`}>
            <MasterSearchInput
              id="users-search"
              value={search}
              onChange={setSearch}
              placeholder="Search user, email, role…"
            />
            <NativeSelect
              className="h-8 w-28"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </NativeSelect>
            <NativeSelect
              className="h-8 w-32"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">All roles</option>
              {roles.map((r) => (
                <option key={r.id} value={r.code}>
                  {r.name}
                </option>
              ))}
            </NativeSelect>
          </MasterFilterBar>

          <div className="overflow-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Dept</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground">
                      No matches
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <div className="font-medium">{displayName(row.fname, row.lname)}</div>
                        <div className="font-mono text-2xs text-muted-foreground">{row.userid}</div>
                      </TableCell>
                      <TableCell className="text-xs">{row.email || "—"}</TableCell>
                      <TableCell className="text-xs">{row.department_name || "—"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {row.role_names.length ? (
                            row.role_names.map((name) => (
                              <Badge key={name} variant="secondary" className="text-2xs">
                                {name}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-2xs text-muted-foreground">None</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusDot tone={row.is_active ? "success" : "neutral"}>
                          {row.is_active ? "Active" : "Inactive"}
                        </StatusDot>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-0.5">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            disabled={!canManage}
                            onClick={() => openEdit(row)}
                            aria-label="Edit profile"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            disabled={!canManage}
                            onClick={() => openRoles(row)}
                            aria-label="Assign roles"
                          >
                            <Shield className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit profile</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void submitProfile();
            }}
          >
            <FieldGrid>
              <Field label="User ID" required span={4} error={form.formState.errors.userid?.message}>
                <Input {...form.register("userid")} />
              </Field>
              <Field label="First" required span={4} error={form.formState.errors.fname?.message}>
                <Input {...form.register("fname")} />
              </Field>
              <Field label="Last" required span={4} error={form.formState.errors.lname?.message}>
                <Input {...form.register("lname")} />
              </Field>
              <Field label="Email" span={6}>
                <Input type="email" {...form.register("email")} />
              </Field>
              <Field label="Phone" span={6}>
                <Input {...form.register("phone")} />
              </Field>
              <Field label="Department" span={8}>
                <NativeSelect {...form.register("department_id")}>
                  <option value="">—</option>
                  {(departmentsQ.data ?? []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <ToggleField
                label="Active"
                span={4}
                checked={form.watch("is_active")}
                onChange={(v) => form.setValue("is_active", v, { shouldDirty: true })}
              />
            </FieldGrid>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={updateProfile.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(rolesUser)} onOpenChange={(o) => !o && setRolesUser(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              Roles — {rolesUser ? displayName(rolesUser.fname, rolesUser.lname) : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-1">
            {roles.map((role) => {
              const checked = selectedRoleIds.includes(role.id);
              return (
                <label
                  key={role.id}
                  className="flex cursor-pointer items-start gap-2 rounded border border-border px-2 py-1.5 text-sm hover:bg-muted/40"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={checked}
                    onChange={(e) => {
                      setSelectedRoleIds((prev) =>
                        e.target.checked
                          ? [...prev, role.id]
                          : prev.filter((id) => id !== role.id),
                      );
                    }}
                  />
                  <span>
                    <span className="font-medium">{role.name}</span>
                    <span className="ml-1 font-mono text-2xs text-muted-foreground">{role.code}</span>
                  </span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={() => setRolesUser(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={setRoles.isPending}
              onClick={() => void submitRoles()}
            >
              Save roles
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PermissionsTab({
  roles,
  permissions,
  modules,
  rolePerms,
  isLoading,
  error,
  canManage,
}: {
  roles: { id: string; name: string; code: string }[];
  permissions: {
    id: string;
    module_id: string;
    name: string;
    code: string;
    module_code?: string | null;
    module_name?: string | null;
  }[];
  modules: { id: string; name: string; code: string }[];
  rolePerms: { role_id: string; permission_id: string }[];
  isLoading: boolean;
  error: Error | null;
  canManage: boolean;
}) {
  const toggle = useToggleRolePermission();
  const [search, setSearch] = useState("");

  const granted = useMemo(() => {
    const set = new Set(rolePerms.map((r) => `${r.role_id}:${r.permission_id}`));
    return set;
  }, [rolePerms]);

  const moduleName = useCallback(
    (moduleId: string, fallback?: string | null) => {
      return modules.find((m) => m.id === moduleId)?.name ?? fallback ?? "Other";
    },
    [modules],
  );

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = permissions.filter((p) => {
      if (!q) return true;
      return [p.code, p.name, p.module_name, p.module_code]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
    const map = new Map<string, typeof rows>();
    for (const p of rows) {
      const key = moduleName(p.module_id, p.module_name);
      const list = map.get(key) ?? [];
      list.push(p);
      map.set(key, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [permissions, search, moduleName]);

  useMasterListShortcuts({ searchInputId: "perms-search" });

  if (error) return <MasterBanner tone="danger">{error.message}</MasterBanner>;
  if (isLoading) {
    return <p className="px-1 py-6 text-sm text-muted-foreground">Loading permissions…</p>;
  }

  return (
    <>
      <MasterFilterBar shownLabel={`${permissions.length} permissions`}>
        <MasterSearchInput
          id="perms-search"
          value={search}
          onChange={setSearch}
          placeholder="Filter permission code…"
        />
      </MasterFilterBar>

      <div className="overflow-auto rounded-md border border-border">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-2xs uppercase tracking-wide text-muted-foreground">
              <th className="sticky left-0 z-10 bg-muted/40 px-2 py-1.5 min-w-[200px]">Permission</th>
              {roles.map((r) => (
                <th key={r.id} className="px-2 py-1.5 text-center min-w-[88px]" title={r.code}>
                  {r.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped.map(([mod, perms]) => (
              <ModuleGroup key={mod} title={mod}>
                {perms.map((p) => (
                  <tr key={p.id} className="border-b border-border/70 hover:bg-muted/20">
                    <td className="sticky left-0 z-10 bg-card px-2 py-1">
                      <div className="font-mono text-2xs">{p.code}</div>
                      <div className="text-2xs text-muted-foreground">{p.name}</div>
                    </td>
                    {roles.map((r) => {
                      const key = `${r.id}:${p.id}`;
                      const checked = granted.has(key);
                      const pending =
                        toggle.isPending &&
                        toggle.variables?.roleId === r.id &&
                        toggle.variables?.permissionId === p.id;
                      return (
                        <td key={r.id} className="px-2 py-1 text-center">
                          <input
                            type="checkbox"
                            disabled={!canManage || pending}
                            checked={checked}
                            onChange={(e) =>
                              toggle.mutate({
                                roleId: r.id,
                                permissionId: p.id,
                                enabled: e.target.checked,
                              })
                            }
                            aria-label={`${r.code} ${p.code}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </ModuleGroup>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-1 text-2xs text-muted-foreground">
        Admin role bypasses all checks in RLS via <code>has_permission</code>. Matrix still stores
        explicit grants for documentation.
      </p>
    </>
  );
}

function ModuleGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <tr className="bg-muted/30">
        <td
          colSpan={99}
          className="sticky left-0 px-2 py-1 text-2xs font-semibold uppercase tracking-wide text-muted-foreground"
        >
          {title}
        </td>
      </tr>
      {children}
    </>
  );
}

function InviteUserDialog({
  open,
  onOpenChange,
  roles,
  onInvited,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: { id: string; name: string; code: string }[];
  onInvited: () => void;
}) {
  const [email, setEmail] = useState("");
  const [fname, setFname] = useState("");
  const [lname, setLname] = useState("");
  const [userid, setUserid] = useState("");
  const [roleCodes, setRoleCodes] = useState<string[]>(["operator"]);
  const [pending, setPending] = useState(false);

  async function submit() {
    setPending(true);
    try {
      await apiJson("/admin/users/invite", {
        method: "POST",
        body: JSON.stringify({
          email,
          fname,
          lname,
          userid: userid || undefined,
          role_codes: roleCodes,
        }),
      });
      toast.success("Invite sent");
      onOpenChange(false);
      setEmail("");
      setFname("");
      setLname("");
      setUserid("");
      setRoleCodes(["operator"]);
      onInvited();
    } catch (e) {
      toast.error((e as Error).message || "Invite failed");
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Invite user</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <FieldGrid>
            <Field label="Email" required span={12}>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ops@example.com"
              />
            </Field>
            <Field label="First" span={4}>
              <Input value={fname} onChange={(e) => setFname(e.target.value)} />
            </Field>
            <Field label="Last" span={4}>
              <Input value={lname} onChange={(e) => setLname(e.target.value)} />
            </Field>
            <Field label="User ID" span={4} hint="Optional">
              <Input
                value={userid}
                onChange={(e) => setUserid(e.target.value)}
                placeholder="from email"
              />
            </Field>
          </FieldGrid>
          <div className="space-y-1.5">
            <div className="text-2xs font-medium text-muted-foreground">Roles</div>
            {roles.map((role) => {
              const checked = roleCodes.includes(role.code);
              return (
                <label
                  key={role.id}
                  className="flex cursor-pointer items-center gap-2 rounded border border-border px-2 py-1.5 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      setRoleCodes((prev) =>
                        e.target.checked
                          ? [...prev, role.code]
                          : prev.filter((c) => c !== role.code),
                      )
                    }
                  />
                  {role.name}
                  <span className="font-mono text-2xs text-muted-foreground">{role.code}</span>
                </label>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={pending || !email.trim()}
            onClick={() => void submit()}
          >
            Send invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
